var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import globalStore from '../store';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import Label from '../components/Label';
import '../styles/widgets/diceManipulative.scss';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import MathEditor from '../components/MathEditor';
import { playAudio, stopAudio } from '../helper/audioHelper';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { announce } from '../helper/announcer';
import { t } from 'i18next';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { getImageSrc } from '../helper/imageBlobCache';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
/** Convert raw LaTeX to screen-reader-friendly text */
function latexToReadable(latex) {
    if (!latex)
        return '';
    var work = latex.replace(/^\\\(/, '').replace(/\\\)$/, '').trim();
    // Fractions
    work = work.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '$1 over $2');
    work = work.replace(/\\frac(\d)(\d)/g, '$1 over $2');
    // Exponents
    work = work.replace(/\^\{([^}]*)\}/g, ' to the power of $1');
    work = work.replace(/\^(\d)/g, ' to the power of $1');
    // Operators
    work = work.replace(/\\times/g, 'times');
    work = work.replace(/\\div/g, 'divided by');
    work = work.replace(/\\cdot/g, 'times');
    work = work.replace(/\\pm/g, 'plus or minus');
    // Comparisons
    work = work.replace(/\\le(?:q)?/g, 'less than or equal to');
    work = work.replace(/\\ge(?:q)?/g, 'greater than or equal to');
    work = work.replace(/\\lt/g, 'less than');
    work = work.replace(/\\gt/g, 'greater than');
    work = work.replace(/\\ne(?:q)?/g, 'not equal to');
    // Symbols
    work = work.replace(/\\%/g, 'percent');
    work = work.replace(/\\\$/g, 'dollars');
    work = work.replace(/\\pi/g, 'pi');
    work = work.replace(/\\sqrt\{([^}]*)\}/g, 'square root of $1');
    // Spacing and placeholders
    work = work.replace(/\\;/g, ' ');
    work = work.replace(/\\,/g, ' ');
    work = work.replace(/\\placeholder\{[^}]*\}/g, '');
    // Clean up
    work = work.replace(/[{}]/g, '');
    work = work.replace(/\\/g, '');
    work = work.replace(/\s+/g, ' ').trim();
    return work;
}
/** Extract readable text from a face's frontImage array (string, math, or mixed) */
function extractFaceText(frontImage) {
    if (!frontImage || frontImage.length === 0)
        return '';
    return frontImage
        .map(function (item) {
        if (item.type === 'math') {
            return latexToReadable(item.latex || item.value || '');
        }
        return (item.value || '').replace(/\u200B/g, '').trim();
    })
        .join(' ')
        .trim();
}
var DiceManipulative = forwardRef(function (props, ref) {
    var _a;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var id = props.id, diceType = props.diceType, faces = props.faces, activeFaceIndex = props.activeFaceIndex, center = props.center, dimensions = props.dimensions, scale = props.scale, groupId = props.groupId, _l = props.isGroup, isGroup = _l === void 0 ? false : _l, _m = props.color, color = _m === void 0 ? 'BLUE' : _m, _o = props.disabled, disabled = _o === void 0 ? false : _o, label = props.label, _p = props.lock, positionLock = _p.positioning, editingLock = _p.editing, _q = props.diceShadowImageSrc, diceShadowImageSrc = _q === void 0 ? './assets/images/manipulative/dice/diceShadowMain.svg' : _q, _r = props.textSize, textSize = _r === void 0 ? 25 : _r, fontSizeOverride = props.fontSizeOverride, _s = props.isWhiteBoard, isWhiteBoard = _s === void 0 ? true : _s;
    /** ---------------- GLOBAL CONFIG ---------------- */
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var bottomToolConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).bottomToolConfig;
    /** ---------------- DATA STORE FROM GAME CONTROLLER ---------------- */
    var _t = useContext(DataContext), selectedTool = _t.selectedTool, setSelectedTool = _t.setSelectedTool, setManipulative = _t.setManipulative, manipulative = _t.manipulative, setActiveManipulator = _t.setActiveManipulator, setManiplulatorComponentVisible = _t.setManiplulatorComponentVisible, setSelectedManipulativeId = _t.setSelectedManipulativeId, panningEnable = _t.panningEnable, contextMenu = _t.contextMenu, setContextMenu = _t.setContextMenu;
    var _u = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _u.startMultiDrag, moveMultiDrag = _u.moveMultiDrag, endMultiDrag = _u.endMultiDrag;
    var duplicateLabel = useDuplicateAnnounce(id, t('text.dice'));
    var creationLabel = useCreationAnnounce(id, t('text.manipulativeDice'));
    /** ---------------- DEFAULT DICE DIMENSION ---------------- */
    var fallbackDimension = isWhiteBoard
        ? {
            width: 96,
            height: 89,
        }
        : {
            width: 128,
            height: 117,
        };
    // const fallbackDimension = isWhiteBoard
    //   ? { width: 64, height: 59 }
    //   : { width: 128, height: 117 };
    var defaultDimension = (_b = props.defaultDimensions) !== null && _b !== void 0 ? _b : fallbackDimension;
    /** ---------------- DEFAULT DICE IMAGES ---------------- */
    var diceFaces = {
        1: {
            blue: './assets/images/manipulative/dice/dice_1.svg',
            white: './assets/images/manipulative/dice/dice_1_white.svg',
        },
        2: {
            blue: './assets/images/manipulative/dice/dice_2.svg',
            white: './assets/images/manipulative/dice/dice_2_white.svg',
        },
        3: {
            blue: './assets/images/manipulative/dice/dice_3.svg',
            white: './assets/images/manipulative/dice/dice_3_white.svg',
        },
        4: {
            blue: './assets/images/manipulative/dice/dice_4.svg',
            white: './assets/images/manipulative/dice/dice_4_white.svg',
        },
        5: {
            blue: './assets/images/manipulative/dice/dice_5.svg',
            white: './assets/images/manipulative/dice/dice_5_white.svg',
        },
        6: {
            blue: './assets/images/manipulative/dice/dice_6.svg',
            white: './assets/images/manipulative/dice/dice_6_white.svg',
        },
    };
    var colorArr = {
        white: '#ffffff',
        blue: '#293273',
    };
    var diceSpriteSheet = {
        RED: {
            path: './assets/images/manipulative/dice/diceSpritesheet_red.png',
            textColor: 'white',
        },
        BLUE: {
            path: './assets/images/manipulative/dice/diceSpritesheet_blue.png',
            textColor: 'blue',
        },
        GREEN: {
            path: './assets/images/manipulative/dice/diceSpritesheet_green.png',
            textColor: 'blue',
        },
        // pending assign new image path
        ORANGE: {
            path: './assets/images/manipulative/dice/diceSpritesheet_orange.png',
            textColor: 'blue',
        },
        YELLOW: {
            path: './assets/images/manipulative/dice/diceSpritesheet_yellow.png',
            textColor: 'blue',
        },
        BLACK: {
            path: './assets/images/manipulative/dice/diceSpritesheet_black.png',
            textColor: 'white',
        },
        BROWN: {
            path: './assets/images/manipulative/dice/diceSpritesheet_brown.png',
            textColor: 'white',
        },
        PURPLE: {
            path: './assets/images/manipulative/dice/diceSpritesheet_purple.png',
            textColor: 'white',
        },
        WHITE: {
            path: './assets/images/manipulative/dice/diceSpritesheet_white.png',
            textColor: 'blue',
        },
        GRAY: {
            path: './assets/images/manipulative/dice/diceSpritesheet_gray.png',
            textColor: 'white',
        },
        DARK_NAVY_BLUE: {
            path: './assets/images/manipulative/dice/diceSpritesheet_blue.png',
            textColor: 'white',
        },
    };
    /** ---------------- CONVERT TEXT FACE INTO A MAP: {1: "", 2: "", ...} ---------------- */
    var customTextFaces = useMemo(function () {
        if (!faces || faces.length === 0)
            return { 1: [] };
        var mapped = {};
        faces.forEach(function (t, i) {
            var _a;
            mapped[i + 1] = ((_a = t.frontImage) !== null && _a !== void 0 ? _a : []).filter(function (item) { return item.type !== 'text' || item.value !== '\u200B'; });
        });
        return mapped;
    }, [faces]);
    /** ---------------- ALLOWED FACE [1, ...LENGTH] ---------------- */
    var allowedFaces = useMemo(function () {
        var _a;
        var len = (_a = faces === null || faces === void 0 ? void 0 : faces.length) !== null && _a !== void 0 ? _a : 0;
        return Array.from({ length: len }, function (_, i) { return i + 1; });
    }, [faces]);
    /** ---------------- STATE ---------------- */
    var _v = useState(activeFaceIndex !== null && activeFaceIndex !== void 0 ? activeFaceIndex : 1), currentFace = _v[0], setCurrentFace = _v[1];
    var _w = useState(false), animate = _w[0], setAnimate = _w[1];
    var _x = useState(true), showDiceTextImg = _x[0], setShowDiceTextImg = _x[1];
    var _y = useState(center), dicePosition = _y[0], setDicePosition = _y[1];
    var _z = useState(false), isFocused = _z[0], setIsFocused = _z[1];
    var isFocusedRef = useRef(false);
    var _0 = useState(false), isDraggingSelf = _0[0], setIsDraggingSelf = _0[1];
    var _1 = useState(null), dragStart = _1[0], setDragStart = _1[1];
    var _2 = useState(scale), diceScale = _2[0], setDiceScale = _2[1];
    var _3 = useState(dimensions), diceSize = _3[0], setDiceSize = _3[1];
    var _4 = useState(null), dragStartItem = _4[0], setDragStartItem = _4[1];
    // DOM-based font size measurement across all text faces
    var BASE_CALC_FONT_SIZE = 100;
    var measureContainerRef = useRef(null);
    var measureFaceQueueRef = useRef([]);
    var measuredFaceSizesRef = useRef({});
    var measureTimerRef = useRef(null);
    var prevDiceScaleRef = useRef(diceScale);
    var baseFontSizeRef = useRef(null);
    var baseDiceScaleRef = useRef(diceScale);
    var _5 = useState(-1), measureFaceTarget = _5[0], setMeasureFaceTarget = _5[1];
    var _6 = useState(null), calculatedFontSize = _6[0], setCalculatedFontSize = _6[1];
    var _7 = useState(false), textAdjusted = _7[0], setTextAdjusted = _7[1];
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    // Screen-reader announcement for roll result
    var _8 = useState(''), announceText = _8[0], setAnnounceText = _8[1];
    // Ref for display .diceData — used for overflow-based scale fallback
    var diceTextDisplayRef = useRef(null);
    var measureFaceAtBase = useCallback(function () {
        var container = measureContainerRef.current;
        if (!container)
            return BASE_CALC_FONT_SIZE;
        var diceDataEl = container.querySelector('.diceData');
        if (!diceDataEl)
            return BASE_CALC_FONT_SIZE;
        var editor = diceDataEl.querySelector('.editorDiv');
        if (!editor)
            return BASE_CALC_FONT_SIZE;
        var margin = diceDataEl.clientWidth * 0.1;
        var availW = diceDataEl.clientWidth - margin * 2;
        var availH = diceDataEl.clientHeight - margin * 2;
        if (availW <= 0 || availH <= 0)
            return BASE_CALC_FONT_SIZE;
        var origW = editor.style.width;
        var origH = editor.style.height;
        var origOv = editor.style.overflow;
        editor.style.width = 'max-content';
        editor.style.height = 'max-content';
        editor.style.overflow = 'visible';
        var cW = editor.scrollWidth;
        var cH = editor.scrollHeight;
        editor.style.width = origW;
        editor.style.height = origH;
        editor.style.overflow = origOv;
        if (cW === 0 || cH === 0)
            return BASE_CALC_FONT_SIZE;
        var scale = Math.min(availW / cW, availH / cH);
        return Math.max(1, Math.floor(BASE_CALC_FONT_SIZE * scale));
    }, []);
    // Start measurement cycle whenever text faces change
    useEffect(function () {
        var scaleRatio = diceScale / prevDiceScaleRef.current;
        prevDiceScaleRef.current = diceScale;
        if (diceType !== 'text') {
            setCalculatedFontSize(null);
            return;
        }
        var faceKeys = Object.keys(customTextFaces)
            .map(Number)
            .filter(function (k) {
            var _a;
            return (_a = customTextFaces[k]) === null || _a === void 0 ? void 0 : _a.some(function (item) {
                return item.type === 'math'
                    ? (item.latex || '').trim().length > 0
                    : (item.value || '').trim().length > 0;
            });
        });
        if (faceKeys.length === 0) {
            setCalculatedFontSize(null);
            return;
        }
        // Immediately scale font from base — avoids rounding drift from incremental Math.floor
        if (scaleRatio !== 1 && baseFontSizeRef.current !== null) {
            var newSize = Math.floor(baseFontSizeRef.current * (diceScale / baseDiceScaleRef.current));
            setCalculatedFontSize(newSize);
            return;
        }
        measuredFaceSizesRef.current = {};
        measureFaceQueueRef.current = faceKeys.slice(1);
        setMeasureFaceTarget(faceKeys[0]);
    }, [diceType, diceScale, JSON.stringify(customTextFaces)]);
    // Measure current face then advance to next
    useEffect(function () {
        var _a;
        if (measureFaceTarget < 0)
            return;
        clearTimeout((_a = measureTimerRef.current) !== null && _a !== void 0 ? _a : undefined);
        measureTimerRef.current = setTimeout(function () {
            var size = measureFaceAtBase();
            measuredFaceSizesRef.current[measureFaceTarget] = size;
            var queue = measureFaceQueueRef.current;
            if (queue.length > 0) {
                measureFaceQueueRef.current = queue.slice(1);
                setMeasureFaceTarget(queue[0]);
            }
            else {
                var allSizes = Object.values(measuredFaceSizesRef.current);
                var baseSize = allSizes.length > 0 ? Math.min.apply(Math, allSizes) : null;
                baseFontSizeRef.current = baseSize;
                baseDiceScaleRef.current = diceScale;
                setCalculatedFontSize(baseSize);
                setMeasureFaceTarget(-1);
            }
        }, 100);
        return function () { var _a; return clearTimeout((_a = measureTimerRef.current) !== null && _a !== void 0 ? _a : undefined); };
    }, [measureFaceTarget, measureFaceAtBase]);
    // Fallback: after render, if text overflows the display .diceData, apply
    // transform: scale() on the .displayDice wrapper so it fits visually.
    // Polls until MathEditor content is ready (retries if content is 0x0).
    useEffect(function () {
        if (diceType !== 'text' || !showDiceTextImg)
            return;
        if (isWhiteBoard)
            setTextAdjusted(false);
        var cancelled = false;
        var retries = 0;
        var maxRetries = 5;
        var measure = function () {
            if (cancelled)
                return;
            var container = diceTextDisplayRef.current;
            if (!container)
                return;
            var displayDice = container.querySelector('.displayDice');
            if (!displayDice)
                return;
            var editor = container.querySelector('.editorDiv');
            if (!editor)
                return;
            // Reset previous scale so measurements are accurate
            displayDice.style.transform = '';
            var margin = container.clientWidth * 0.1;
            var availW = container.clientWidth - margin * 2;
            var availH = container.clientHeight - margin * 2;
            if (availW <= 0 || availH <= 0)
                return;
            // Temporarily let editor expand to natural size for measurement
            var origW = editor.style.width;
            var origWS = editor.style.whiteSpace;
            editor.style.width = 'max-content';
            editor.style.whiteSpace = 'nowrap';
            var cW = editor.scrollWidth;
            var cH = editor.scrollHeight;
            editor.style.width = origW;
            editor.style.whiteSpace = origWS;
            // MathEditor hasn't populated content yet — retry
            if (cW <= 0 || cH <= 0) {
                if (retries < maxRetries) {
                    retries++;
                    setTimeout(measure, 100);
                }
                return;
            }
            if (cW <= availW && cH <= availH) {
                setTextAdjusted(true); // already fits — no scale needed
                return;
            }
            var scaleFactor = Math.min(availW / cW, availH / cH, 1);
            displayDice.style.transform = "scale(".concat(scaleFactor, ")");
            displayDice.style.transformOrigin = 'center center';
            setTextAdjusted(true);
        };
        setTimeout(measure, 50);
        return function () {
            cancelled = true;
        };
    }, [
        diceType,
        showDiceTextImg,
        currentFace,
        calculatedFontSize,
        JSON.stringify(customTextFaces),
    ]);
    /** ---------------- REF ---------------- */
    var diceInteractiveRef = useRef(null);
    var dicePosRef = useRef(dicePosition);
    var dragStartRef = useRef(null);
    var hasMoved = useRef(false);
    var wasRightClick = useRef(false);
    var dragStated = useRef(false);
    var rollIntervalRef = useRef(null);
    // Touch devices: deferred roll timer so SelectFocusOverlay click can cancel it
    var deferredRollTimer = useRef(null);
    var dieNumber = useMemo(function () {
        var dice = manipulative.filter(function (m) { return m.data.type === 'dice'; });
        var idx = dice.findIndex(function (m) { return m.data.id === id; });
        return idx === -1 ? -1 : idx + 1;
    }, [manipulative, id]);
    /** ---------------- GET RANDOM FACES ---------------- */
    var getRandomAllowedFace = function () {
        var randomIndex = Math.floor(Math.random() * allowedFaces.length);
        return allowedFaces[randomIndex];
    };
    /** ---------------- ROLL DICE ANIMATION ---------------- */
    var rollDice = function (onComplete) {
        if (animate)
            return;
        // Clear any existing interval
        if (rollIntervalRef.current) {
            clearInterval(rollIntervalRef.current);
            rollIntervalRef.current = null;
        }
        setShowDiceTextImg(false);
        setAnimate(true);
        playAudio('diceRoll');
        var rollCount = 0;
        var finalFace = getRandomAllowedFace();
        setCurrentFace(finalFace);
        setTimeout(function () { return setShowDiceTextImg(true); }, 1800);
        // Skip individual announcement when grouped — ManipulativeGroup announces all results
        // Skip individual announcement when grouped — ManipulativeGroup announces all results
        if (!isGroup) {
            // Clear immediately so the live region resets; re-setting text in the timeout
            // is always a state change ('' → new text), even if the same face is rolled.
            setAnnounceText('');
            setTimeout(function () {
                // Announce only the roll event — the face value is already exposed via
                // the button's aria-label (which updates when currentFace changes), so
                // we avoid restating it here to prevent JAWS reading it twice.
                setAnnounceText(t('text.rolledAnnounce'));
                // if (diceType === 'dot') {
                //   setAnnounceText(
                //     t('text.rolledFaceAnnounce', { face: finalFace, sides: allowedFaces.length }),
                //   );
                // } else {
                //   // Text/number dice: read the face value (handles plain text AND math LaTeX)
                //   const readable = extractFaceText(faces?.[finalFace - 1]?.frontImage as any);
                //   const faceValue = readable || t('text.blank');
                //   setAnnounceText(t('text.rolledValueAnnounce', { value: faceValue }));
                // }
            }, 1850);
        }
        var oldDiceItem;
        var newDiceItem;
        if (isGroup) {
            // When grouped, don't update manipulative state here — ManipulativeGroup
            // collects all roll results and does a single setManipulative call.
            var currentItem = manipulative.find(function (item) { return item.data.id === id; });
            if (currentItem) {
                oldDiceItem = currentItem;
                newDiceItem = __assign(__assign({}, currentItem), { data: __assign(__assign({}, currentItem.data), { activeFaceIndex: finalFace }) });
            }
        }
        else {
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.id === id) {
                        oldDiceItem = item;
                        newDiceItem = __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { activeFaceIndex: finalFace }) });
                        return newDiceItem;
                    }
                    return item;
                });
            });
        }
        if (oldDiceItem && newDiceItem) {
            onComplete === null || onComplete === void 0 ? void 0 : onComplete({ oldDiceItem: oldDiceItem, newDiceItem: newDiceItem });
        }
        // rollIntervalRef.current = setInterval(() => {
        //   rollCount++;
        //   const randomFace = getRandomAllowedFace();
        //   // setCurrentFace(randomFace);
        //   if (rollCount > 12) {
        //     if (rollIntervalRef.current) {
        //       clearInterval(rollIntervalRef.current);
        //       rollIntervalRef.current = null;
        //     }
        //     // exact match with 0.9s animation
        //     // setTimeout(() => setAnimate(false), 900);
        //   }
        // }, 60);
    };
    // Cleanup interval on unmount
    useEffect(function () {
        return function () {
            if (rollIntervalRef.current) {
                clearInterval(rollIntervalRef.current);
                rollIntervalRef.current = null;
            }
        };
    }, []);
    /** ---------------- SET ACTIVE FACE INDEX WITH IT'S CLOSEST INDEX ---------------- */
    useEffect(function () {
        if (activeFaceIndex == null)
            return;
        var wantedFace = activeFaceIndex;
        if (allowedFaces.includes(wantedFace)) {
            setCurrentFace(wantedFace);
            return;
        }
        var nearest = allowedFaces[0];
        var minDiff = Infinity;
        allowedFaces.forEach(function (face) {
            var diff = Math.abs(face - wantedFace);
            if (diff < minDiff) {
                minDiff = diff;
                nearest = face;
            }
        });
        setCurrentFace(nearest);
    }, [activeFaceIndex, allowedFaces]);
    /** ---------------- SET DICE POSITION ---------------- */
    // Always sync internal dice position with the incoming `center` prop so that
    // dice rendered inside a group follow group moves as their `center` updates.
    useEffect(function () {
        if (center && !isGroup)
            setDicePosition(center);
    }, [center, isGroup]);
    /** ---------------- IS FOCUSED ---------------- */
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        isFocusedRef.current = nowFocused;
        // Auto-focus the die when newly selected via setSelectedTool (e.g. after popup Done)
        // Skip if context menu is open — it manages its own focus
        if (nowFocused &&
            !wasFocused &&
            !disabled &&
            !isGroup &&
            diceInteractiveRef.current &&
            !contextMenu) {
            setTimeout(function () {
                var _a;
                (_a = diceInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
    }, [selectedTool]);
    /** ---------------- DRAG START ---------------- */
    var onMouseDown = function (e) {
        // Prevent default touch behavior to avoid page refresh/scrolling on iPad
        if ('touches' in e) {
            e.preventDefault();
        }
        if (disabled)
            return;
        // Track right-click so stopDrag doesn't roll the dice
        wasRightClick.current = 'button' in e && e.button === 2;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (animate)
            return;
        if (!panningEnable) {
            var foundItem = manipulative.find(function (m) { return m.data.type === 'dice' && m.data.id === id; });
            setDragStartItem(foundItem ? JSON.parse(JSON.stringify(foundItem.data.center)) : null);
            dragStartRef.current = {
                x: clientX,
                y: clientY,
            };
            hasMoved.current = false;
            setDragStart({
                x: clientX - dicePosition.x * zoomLevel,
                y: clientY - dicePosition.y * zoomLevel,
            });
            dragStated.current = true;
            setIsDraggingSelf(true);
            startMultiDrag();
        }
    };
    /** ---------------- HANDLE DRAG, ROLL ON CLICK ---------------- */
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b;
            // Prevent default touch behavior to avoid page refresh/scrolling on iPad
            if ('touches' in e) {
                e.preventDefault();
            }
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            if (!dragStartRef.current)
                return;
            // movement threshold
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMoved.current = true;
            }
            if (!hasMoved.current)
                return;
            if (positionLock)
                return;
            // Only update local position during drag for visual feedback.
            // Context (setManipulative) is synced once on drop in stopDrag.
            var newX = (clientX - ((_a = dragStart === null || dragStart === void 0 ? void 0 : dragStart.x) !== null && _a !== void 0 ? _a : 0)) / zoomLevel;
            var newY = (clientY - ((_b = dragStart === null || dragStart === void 0 ? void 0 : dragStart.y) !== null && _b !== void 0 ? _b : 0)) / zoomLevel;
            setDicePosition({ x: newX, y: newY });
            if (dragStartItem) {
                moveMultiDrag(newX - dragStartItem.x, newY - dragStartItem.y);
            }
        };
        var stopDrag = function () {
            if (!dragStart)
                return;
            setIsDraggingSelf(false);
            if (!hasMoved.current && !wasRightClick.current) {
                var doRoll = function () {
                    rollDice(function (_a) {
                        var oldDiceItem = _a.oldDiceItem, newDiceItem = _a.newDiceItem;
                        if (oldDiceItem && newDiceItem) {
                            saveState({
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'activeFaceIndex',
                                old: JSON.stringify(oldDiceItem.data.activeFaceIndex),
                                new: JSON.stringify(newDiceItem.data.activeFaceIndex),
                            });
                        }
                    });
                };
                // Touch devices: defer roll so SelectFocusOverlay click can cancel it
                if (IS_TOUCH_DEVICE) {
                    deferredRollTimer.current = setTimeout(doRoll, 100);
                }
                else {
                    doRoll();
                }
            }
            else if (!positionLock) {
                var final = dicePosRef.current;
                var _oldData = dragStartItem !== null ? dragStartItem : undefined;
                var _newData_1 = { x: final.x, y: final.y };
                if (_oldData) {
                    var entry = {
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(_oldData),
                        new: JSON.stringify(_newData_1),
                    };
                    if (!registerMultiSelectSaveEntries(id, [entry])) {
                        saveState(entry);
                    }
                }
                // Sync final position and move die to front of render order so the
                // dropped die draws above its previous neighbors (matches Spinner).
                setManipulative(function (prev) {
                    var index = prev.findIndex(function (item) { return item.data.id === id; });
                    if (index === -1)
                        return prev;
                    var updated = __spreadArray([], prev, true);
                    var movedItem = updated.splice(index, 1)[0];
                    updated.push(__assign(__assign({}, movedItem), { data: __assign(__assign({}, movedItem.data), { center: _newData_1 }) }));
                    return updated;
                });
            }
            dragStated.current = false;
            dragStartRef.current = null;
            setDragStart(null);
            endMultiDrag();
        };
        if (dragStated.current && dragStart !== null && !isGroup && !panningEnable && !disabled) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', stopDrag);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', stopDrag);
            window.addEventListener('touchcancel', stopDrag);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDrag);
            window.removeEventListener('touchcancel', stopDrag);
        };
    }, [
        dragStart,
        isGroup,
        isFocused,
        panningEnable,
        disabled,
        JSON.stringify(manipulative),
        JSON.stringify(dragStartItem),
    ]);
    /** ---------------- SYNC DICE SCALE ---------------- */
    useEffect(function () {
        setDiceScale(scale);
    }, [scale]);
    /** ---------------- RECALCULATE DIMENSION WHEN SCALE ---------------- */
    useEffect(function () {
        var newWidth = defaultDimension.width * diceScale;
        var newHeight = defaultDimension.height * diceScale;
        setDiceSize({ width: newWidth, height: newHeight });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { dimensions: { width: newWidth, height: newHeight }, scale: diceScale }) }) : item;
            });
        });
    }, [diceScale]);
    /** ---------------- SYNC DICE DIMENSION ---------------- */
    useEffect(function () {
        setDiceSize(dimensions);
    }, [JSON.stringify(dimensions)]);
    /** ---------------- SYNC DICE DIMENSION REF ---------------- */
    useEffect(function () {
        dicePosRef.current = dicePosition;
    }, [dicePosition]);
    /** ---------------- CONDITION FOR DOT ---------------- */
    var isDotDice = diceType === 'dot';
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var changeColour = function (data) {
            var _oldColor;
            var _newColor;
            var _newState = manipulative.map(function (item) {
                if (item.data.id === id) {
                    _oldColor = item.data.color;
                    _newColor = data.colorKey;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { color: _newColor }) });
                }
                else
                    return item;
            });
            if (_oldColor && _newColor) {
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'color',
                    old: JSON.stringify(_oldColor),
                    new: JSON.stringify(_newColor),
                });
            }
            setManipulative(function (prev) { return _newState; });
        };
        var onToolClick = function (data) {
            switch (data.id) {
                case 'edit':
                    setActiveManipulator('dice');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'scaleMinus': {
                    var _oldScaleMinus = diceScale;
                    var _newScaleMinus_1 = Math.max(0.5, diceScale - 0.1);
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(_oldScaleMinus),
                        new: JSON.stringify(_newScaleMinus_1),
                    });
                    setDiceScale(function () { return _newScaleMinus_1; });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.dice'),
                        direction: t('text.down'),
                        value: _newScaleMinus_1.toFixed(1),
                    }));
                    break;
                }
                case 'scalePlus': {
                    var _oldScalePlus = diceScale;
                    var _newScalePlus_1 = diceScale + 0.1;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(_oldScalePlus),
                        new: JSON.stringify(_newScalePlus_1),
                    });
                    setDiceScale(function () { return _newScalePlus_1; });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.dice'),
                        direction: t('text.up'),
                        value: _newScalePlus_1.toFixed(1),
                    }));
                    break;
                }
            }
        };
        var onRollDice = function (data) {
            if (data.id === id && !animate) {
                rollDice();
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
            customEventHandler.addEventListener('colourChange', changeColour);
        }
        customEventHandler.addEventListener('rollDice', onRollDice);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
            customEventHandler.removeEventListener('colourChange', changeColour);
            customEventHandler.removeEventListener('rollDice', onRollDice);
        };
    }, [isFocused, diceScale, manipulative, animate]);
    /** ---------------- FUNCTIONS FOR PARENT ---------------- */
    useImperativeHandle(ref, function () { return ({
        rollDice: function (onComplete) {
            rollDice(onComplete);
        },
    }); });
    return (_jsxs("div", __assign({ className: "diceWrapper", "data-manipulative-id": id }, (groupId ? { 'data-group-id': groupId } : {}), { role: !isWhiteBoard ? 'presentation' : undefined, style: (_a = {
                position: isGroup ? 'relative' : 'absolute',
                top: isGroup ? 0 : dicePosition.y,
                left: isGroup ? 0 : dicePosition.x,
                fontSize: "".concat(diceScale, "rem")
            },
            _a['--scaleDice'] = "".concat(diceScale),
            _a.zIndex = isDraggingSelf ? 999 : undefined,
            _a), children: [_jsx(SelectFocusOverlay, { id: id, hidden: animate || !isWhiteBoard || isGroup, className: "dieSelectBtn", title: t('text.selectDie'), onAfterClick: IS_TOUCH_DEVICE
                    ? function () {
                        var _a;
                        if (animate)
                            return; // Don't open context menu during roll animation
                        // Cancel any deferred dice roll triggered by VoiceOver's touch events
                        if (deferredRollTimer.current) {
                            clearTimeout(deferredRollTimer.current);
                            deferredRollTimer.current = null;
                        }
                        var rect = (_a = diceInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                        if (rect) {
                            setContextMenu({
                                x: rect.right,
                                y: rect.top,
                                manipulativeId: id,
                            });
                        }
                    }
                    : undefined }), _jsx("div", { ref: diceInteractiveRef, id: "diceWithShadow_".concat(id), className: "diceWithShadow ".concat(animate ? 'atDiceHover' : ''), style: {
                    width: diceSize.width,
                    height: diceSize.height,
                    cursor: animate || disabled
                        ? 'default'
                        : isGroup || panningEnable
                            ? 'inherit'
                            : positionLock
                                ? 'pointer'
                                : dragStated.current
                                    ? 'grabbing'
                                    : 'pointer',
                }, onMouseDown: isWhiteBoard ? onMouseDown : undefined, onTouchStart: isWhiteBoard ? onMouseDown : undefined, tabIndex: disabled || isGroup || !isWhiteBoard ? -1 : 0, role: isGroup || !isWhiteBoard ? 'img' : 'button', "aria-roledescription": isGroup || !isWhiteBoard ? undefined : t('text.die'), "aria-label": (_c = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _c !== void 0 ? _c : (function () {
                    var _a;
                    var isPreview = dieNumber === -1;
                    if (diceType === 'dot') {
                        var label_1 = isGroup
                            ? t('text.rolledDotGroupLabel', { face: currentFace })
                            : isPreview
                                ? t('text.previewDotLabel', { face: currentFace, sides: allowedFaces.length })
                                : t('text.rolledDotLabel', {
                                    number: dieNumber,
                                    face: currentFace,
                                    sides: allowedFaces.length,
                                });
                        if (isGroup)
                            return label_1;
                        return "".concat(label_1).concat(!disabled ? " ".concat(t('text.pressEnterToRoll')) : '');
                    }
                    var readableFaceValue = extractFaceText((_a = faces === null || faces === void 0 ? void 0 : faces[currentFace - 1]) === null || _a === void 0 ? void 0 : _a.frontImage);
                    var isBlank = !readableFaceValue;
                    var faceValue = isBlank ? t('text.blank') : readableFaceValue;
                    var label = isGroup
                        ? t('text.rolledTextGroupLabel', { value: faceValue })
                        : isPreview && isBlank
                            ? t('text.previewTextBlankLabel', { sides: allowedFaces.length })
                            : isPreview
                                ? t('text.previewTextLabel', { value: faceValue, sides: allowedFaces.length })
                                : t('text.rolledTextLabel', {
                                    number: dieNumber,
                                    value: faceValue,
                                    sides: allowedFaces.length,
                                });
                    if (isGroup)
                        return label;
                    return "".concat(label).concat(!disabled ? " ".concat(t('text.pressEnterToRoll')) : '');
                })(), onKeyDown: isWhiteBoard
                    ? function (e) {
                        var _a, _b;
                        if (disabled || animate)
                            return;
                        // Context menu: Enter, Space, Shift+F10, ContextMenu key
                        if ((e.key === 'Enter' ||
                            e.key === ' ' ||
                            e.key === 'ContextMenu' ||
                            (e.shiftKey && e.key === 'F10')) &&
                            !isGroup) {
                            e.preventDefault();
                            if (!selectedTool.includes("".concat(id))) {
                                setSelectedTool(["".concat(id)]);
                            }
                            var rect = (_a = diceInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                            if (rect) {
                                setContextMenu({
                                    x: rect.right,
                                    y: rect.top,
                                    manipulativeId: id,
                                });
                            }
                            return;
                        }
                        // Arrow key movement (only for standalone dice, not inside groups)
                        if (!isGroup &&
                            !panningEnable &&
                            !positionLock &&
                            ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                            e.preventDefault();
                            var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : dicePosition;
                            if (!keyMoveStartRef.current) {
                                keyMoveStartRef.current = __assign({}, curPos);
                            }
                            var step = e.shiftKey ? 50 : 10;
                            var scaledStep = step / zoomLevel;
                            var delta = {
                                ArrowUp: { x: 0, y: -scaledStep },
                                ArrowDown: { x: 0, y: scaledStep },
                                ArrowLeft: { x: -scaledStep, y: 0 },
                                ArrowRight: { x: scaledStep, y: 0 },
                            };
                            var d = delta[e.key];
                            var newCenter_1 = { x: curPos.x + d.x, y: curPos.y + d.y };
                            keyMoveCurPosRef.current = newCenter_1;
                            setDicePosition(newCenter_1);
                            setManipulative(function (prev) {
                                return prev.map(function (item) {
                                    return item.data.id === id
                                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_1 }) }) : item;
                                });
                            });
                            // Debounced undo save + screen reader announcement
                            if (keyMoveSaveTimer.current)
                                clearTimeout(keyMoveSaveTimer.current);
                            keyMoveSaveTimer.current = setTimeout(function () {
                                if (keyMoveStartRef.current && keyMoveCurPosRef.current) {
                                    var oldPos = keyMoveStartRef.current;
                                    var newPos_1 = keyMoveCurPosRef.current;
                                    saveState({
                                        type: id,
                                        id: id.split('_')[1],
                                        propertyName: 'center',
                                        old: JSON.stringify(oldPos),
                                        new: JSON.stringify(newPos_1),
                                    });
                                    var dx = Math.round(newPos_1.x - oldPos.x);
                                    var dy = Math.round(newPos_1.y - oldPos.y);
                                    var directions_1 = [];
                                    if (dy < 0)
                                        directions_1.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                                    if (dy > 0)
                                        directions_1.push("".concat(t('text.down'), " ").concat(dy));
                                    if (dx < 0)
                                        directions_1.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                                    if (dx > 0)
                                        directions_1.push("".concat(t('text.right'), " ").concat(dx));
                                    setAnnounceText('');
                                    requestAnimationFrame(function () {
                                        setAnnounceText(t('text.manipulativeMoved', {
                                            name: t('text.dice'),
                                            direction: directions_1.join(" ".concat(t('text.and'), " ")),
                                            x: Math.round(newPos_1.x),
                                            y: Math.round(newPos_1.y),
                                        }));
                                    });
                                    keyMoveStartRef.current = null;
                                    keyMoveCurPosRef.current = null;
                                }
                            }, 500);
                        }
                        else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                            // Prevent arrow keys from scrolling the stage even when movement is locked
                            e.preventDefault();
                        }
                    }
                    : undefined, children: _jsxs("div", { id: "dice_".concat(id), 
                    // className={`diceDim ${animate ? 'diceRollingTop' : ''}`}
                    className: "diceDim ", onAnimationEnd: function () {
                        setAnimate(false);
                        stopAudio('diceRoll');
                    }, children: [isFocused && !animate && !isGroup && _jsx("div", { className: "diceActiveState" }), _jsx("div", { className: "diceSprite ".concat(animate ? 'rollAnimation' : ''), style: {
                                backgroundImage: "url(".concat(diceSpriteSheet[color].path, ")"),
                            } }), isDotDice && showDiceTextImg && (_jsx("img", { className: "diceDot ".concat(isWhiteBoard ? 'atWhiteBoard' : ''), src: getImageSrc((_d = diceFaces[currentFace]) === null || _d === void 0 ? void 0 : _d[diceSpriteSheet[color].textColor]), alt: "", draggable: false, "aria-hidden": true })), diceType === 'text' && measureFaceTarget >= 0 && (_jsx("div", { ref: measureContainerRef, style: {
                                position: 'absolute',
                                inset: 0,
                                visibility: 'hidden',
                                pointerEvents: 'none',
                            }, "aria-hidden": true, children: _jsx("div", { className: "diceData", style: { fontSize: "".concat(BASE_CALC_FONT_SIZE, "px") }, children: _jsx(MathEditor, { data: {
                                        type: 'data',
                                        data: {
                                            text: customTextFaces[measureFaceTarget],
                                            color: "".concat(colorArr[diceSpriteSheet[color].textColor]),
                                            alignment: 'center',
                                            size: BASE_CALC_FONT_SIZE,
                                        },
                                    }, className: "displayDice", isTextUpdate: false, idPreffix: "diceMeasure-".concat(id), index: 0, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, viewonly: true }) }) })), diceType === 'image' && showDiceTextImg && (_jsx("img", { className: "diceFaceImage ".concat(isWhiteBoard ? 'atWhiteBoard' : ''), src: getImageSrc((_h = (_g = (_f = (_e = faces === null || faces === void 0 ? void 0 : faces[currentFace - 1]) === null || _e === void 0 ? void 0 : _e.frontImage) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.value) !== null && _h !== void 0 ? _h : ''), alt: "", draggable: false, "aria-hidden": true })), diceType === 'text' && (_jsx("div", { ref: diceTextDisplayRef, className: "diceData", style: {
                                fontSize: "".concat((_j = fontSizeOverride !== null && fontSizeOverride !== void 0 ? fontSizeOverride : calculatedFontSize) !== null && _j !== void 0 ? _j : BASE_CALC_FONT_SIZE, "px"),
                                visibility: !showDiceTextImg ||
                                    calculatedFontSize === null ||
                                    (isWhiteBoard && !textAdjusted)
                                    ? 'hidden'
                                    : 'visible',
                            }, "aria-hidden": true, children: _jsx(MathEditor, { data: {
                                    type: 'data',
                                    data: {
                                        text: customTextFaces[currentFace],
                                        color: "".concat(colorArr[diceSpriteSheet[color].textColor]),
                                        alignment: 'center',
                                        size: (_k = fontSizeOverride !== null && fontSizeOverride !== void 0 ? fontSizeOverride : calculatedFontSize) !== null && _k !== void 0 ? _k : BASE_CALC_FONT_SIZE,
                                    },
                                }, className: "displayDice", isTextUpdate: false, idPreffix: "dicePopupImage", index: 0, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, viewonly: true }, "diceMathEditor-".concat(id)) }))] }) }), _jsx(Label, { text: label, flow: true, fontSize: 20 * diceScale }), !isGroup && isWhiteBoard && isFocused && (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText }))] })));
});
export default DiceManipulative;
